import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Share2 } from "lucide-react";

/**
 * /share-target — destination for the Android / iOS Web Share Target.
 *
 * The flow (iter 26.12, 14 May 2026):
 *   1. User taps "Share" in any app, picks "Spy Finance" from the sheet.
 *   2. The browser POSTs the file(s) to /share-target.
 *   3. Our service worker (public/sw.js) intercepts that POST, stashes
 *      each file in a named Cache, and 303-redirects to here with a
 *      ?key=… query string.
 *   4. This component reads files out of the Cache, drops them through
 *      the same /api/shed/shred/stream pipeline the Shredder uses,
 *      shows progress, then redirects to /shredder.
 *
 * No new backend logic — same Shredder, same heartbeats, same dedup,
 * same one-entrance canon. Just a different door.
 */
export default function ShareTarget() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const [items, setItems] = useState([]);      // [{name, status, error}]
    const [phase, setPhase] = useState("loading"); // loading | shredding | done | error
    const [err, setErr] = useState("");
    const ranRef = useRef(false);

    useEffect(() => {
        // Guard against React 18 StrictMode double-invocation
        if (ranRef.current) return;
        ranRef.current = true;
        (async () => {
            const key = params.get("key");
            const text = params.get("text");
            const title = params.get("title");
            const url = params.get("url");

            // Text / URL-only share (no file attached) — file it as a typed note
            if (!key && (text || url || title)) {
                const composed = [title, text, url].filter(Boolean).join("\n").trim();
                if (!composed) { setPhase("done"); return; }
                setItems([{ name: `shared-text-${Date.now()}.txt`, status: "shredding" }]);
                setPhase("shredding");
                try {
                    const blob = new Blob([composed], { type: "text/plain" });
                    const fd = new FormData();
                    fd.append("file", blob, `shared-text-${Date.now()}.txt`);
                    await api.post("/shed/shred/stream", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setItems([{ name: "shared-text", status: "filed" }]);
                    setPhase("done");
                    setTimeout(() => nav("/shredder"), 1500);
                } catch (e) {
                    setErr(e.response?.data?.detail || e.message || "shred failed");
                    setPhase("error");
                }
                return;
            }

            if (!key) {
                // Cold-load — no share in flight. Bounce to the Shredder.
                nav("/shredder", { replace: true });
                return;
            }

            // File share — fish them out of the SW cache
            let files = [];
            try {
                const cache = await caches.open("spyfinance-share-pipe-v1");
                const reqs = await cache.keys();
                const mine = reqs.filter((r) => r.url.includes(`/__shared/${key}/`));
                if (!mine.length) {
                    setErr("Share expired before we could pick it up. Try again from the share sheet.");
                    setPhase("error");
                    return;
                }
                // Sort by the index segment so order is preserved
                mine.sort((a, b) => {
                    const pa = parseInt(a.url.split(`/__shared/${key}/`)[1], 10);
                    const pb = parseInt(b.url.split(`/__shared/${key}/`)[1], 10);
                    return pa - pb;
                });
                for (const r of mine) {
                    const resp = await cache.match(r);
                    if (!resp) continue;
                    const blob = await resp.blob();
                    const name = decodeURIComponent(
                        resp.headers.get("X-Shared-Name") ||
                        r.url.split("/").pop() || "file"
                    );
                    files.push(new File([blob], name, { type: blob.type || "application/octet-stream" }));
                }
                // Clean up the cache so subsequent visits to /share-target
                // don't replay old shares
                for (const r of mine) await cache.delete(r);
            } catch (e) {
                setErr(`Cache read failed: ${e.message}`);
                setPhase("error");
                return;
            }

            if (!files.length) {
                setErr("No files in the share payload.");
                setPhase("error");
                return;
            }

            setItems(files.map((f) => ({ name: f.name, size: f.size, status: "pending" })));
            setPhase("shredding");

            // Process serially through the same endpoint the Shredder uses.
            // Serial (not parallel) here on purpose — a share is usually
            // one file; even when it's a few, the user is on mobile and
            // we'd rather be polite to the LLM budget.
            for (let i = 0; i < files.length; i++) {
                setItems((prev) => prev.map((it, ix) =>
                    ix === i ? { ...it, status: "shredding" } : it));
                try {
                    const fd = new FormData();
                    fd.append("file", files[i]);
                    await api.post("/shed/shred/stream", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 240000,
                    });
                    setItems((prev) => prev.map((it, ix) =>
                        ix === i ? { ...it, status: "filed" } : it));
                } catch (e) {
                    const msg = e.response?.data?.detail || e.message || "shred failed";
                    setItems((prev) => prev.map((it, ix) =>
                        ix === i ? { ...it, status: "failed", error: msg } : it));
                }
            }
            setPhase("done");
            // Auto-bounce to the Shredder once everything's filed — but
            // only if NO failures (operator should see those).
            setTimeout(() => {
                setItems((current) => {
                    if (current.every((it) => it.status === "filed")) {
                        nav("/shredder");
                    }
                    return current;
                });
            }, 1800);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        })();
    }, []);

    return (
        <Shell>
            <div className="mb-6">
                <div className="label-typed tw-prefix">Inbound · Share Sheet Drop</div>
                <h1 className="font-heading text-4xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                    <Share2 className="w-8 h-8 text-stamp-red" strokeWidth={1.5} />
                    Receiving<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-2 max-w-2xl">
                    Shared content lands here, gets routed through the Shredder, and you go back to whatever you were doing.
                </p>
            </div>

            <div className="paper-card p-5" data-testid="share-target-panel">
                {phase === "loading" && (
                    <div className="flex items-center gap-3 text-ink-soft font-body text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Picking up the parcel…
                    </div>
                )}
                {phase === "shredding" && (
                    <div className="flex items-center gap-3 text-stamp-red font-heading uppercase tracking-[0.22em] text-xs mb-4">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Filing through the Shredder
                    </div>
                )}
                {phase === "done" && items.every((it) => it.status === "filed") && (
                    <div className="flex items-center gap-3 text-stamp-green font-heading uppercase tracking-[0.22em] text-xs mb-4" data-testid="share-target-done">
                        <CheckCircle2 className="w-4 h-4" />
                        Filed · returning to the Shredder
                    </div>
                )}
                {phase === "error" && (
                    <div className="flex items-start gap-3 text-stamp-red font-body text-sm mb-4" data-testid="share-target-error">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <pre className="whitespace-pre-wrap text-xs flex-1">{err}</pre>
                    </div>
                )}

                {items.length > 0 && (
                    <ul className="space-y-2" data-testid="share-target-items">
                        {items.map((it, ix) => (
                            <li
                                key={ix}
                                className={`flex items-baseline gap-2 border-l-2 pl-3 py-1.5 ${
                                    it.status === "filed" ? "border-stamp-green"
                                    : it.status === "failed" ? "border-stamp-red"
                                    : it.status === "shredding" ? "border-stamp-red bg-paper-dark/30"
                                    : "border-ink/30"
                                }`}
                                data-testid={`share-target-item-${it.status}`}
                            >
                                {it.status === "shredding" && <Loader2 className="w-3 h-3 animate-spin text-stamp-red" />}
                                {it.status === "filed" && <CheckCircle2 className="w-3 h-3 text-stamp-green" />}
                                {it.status === "failed" && <AlertTriangle className="w-3 h-3 text-stamp-red" />}
                                <span className="font-body text-sm text-ink truncate flex-1">{it.name}</span>
                                {it.size != null && (
                                    <span className="label-typed text-[9px] text-ink-faint">
                                        {(it.size / 1024).toFixed(0)} KB
                                    </span>
                                )}
                                {it.error && (
                                    <span className="label-typed text-[9px] text-stamp-red truncate max-w-[40%]" title={it.error}>
                                        {it.error}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {(phase === "done" || phase === "error") && (
                    <div className="mt-4 flex items-center gap-3">
                        <Link
                            to="/shredder"
                            className="border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper-light px-4 py-2 font-heading uppercase text-[11px] tracking-[0.25em] inline-flex items-center gap-2"
                            data-testid="share-target-go-shredder"
                        >
                            <Upload className="w-3 h-3" /> Open Shredder
                        </Link>
                        <Link
                            to="/ledger"
                            className="border border-ink/40 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-[11px] tracking-[0.25em]"
                        >
                            View Ledger
                        </Link>
                    </div>
                )}
            </div>
        </Shell>
    );
}
